import React, { useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import _ from "lodash";
import { FormHelperText, Box, styled, Tooltip, useTheme } from "@mui/material";
import { tokens } from "../../theme/tokens";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TrackNowCheckbox } from "../ui/TrackNowCheckbox";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { SelectProps } from "@mui/material";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      borderRadius: '10px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
      marginTop: '4px',
    },
    sx: {
      borderRadius: '10px',
      mt: 1,
      '& .MuiMenuItem-root': {
        fontFamily: 'Area Normal, Inter, sans-serif',
        fontSize: '16px',
        fontWeight: 400,
      },
      '& .MuiListItemText-primary': {
        fontFamily: 'Area Normal, Inter, sans-serif',
        fontSize: '16px',
        fontWeight: 400,
      },
    },
  },
};
interface TrackNowSelectProps extends Omit<SelectProps, 'variant'> {
  label?: string;
  helperText?: string;
  success?: boolean;
  successMessage?: string;
  showDisabledTooltip?: boolean;
}
const StyledSelect = styled(Select)<TrackNowSelectProps>(({ theme, success, error }) => ({
  height: '48px',
  borderRadius: tokens.radius.pill,
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  backgroundColor: tokens.colors.offWhite,

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: tokens.colors.midGrey,
    borderWidth: '1px',
    borderRadius: tokens.radius.pill,
  },

  '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main} !important`,
    borderWidth: '2px !important',
    borderRadius: `${tokens.radius.pill}px !important`,
  },

  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: tokens.colors.midGrey,
    borderWidth: '1px',
  },

  '&.Mui-disabled': {
    backgroundColor: tokens.colors.offWhite,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(192, 190, 194, 0.5)',
      borderWidth: '1px',
    },
    '& .MuiSelect-select': {
      color: tokens.colors.grey,
      cursor: 'not-allowed',
      WebkitTextFillColor: tokens.colors.grey,
    },
  },

  '& .MuiSelect-select': {
    padding: '12px 14px',
    color: tokens.colors.offBlack,
    display: 'flex',
    alignItems: 'center',
    minHeight: 'unset',
    boxSizing: 'border-box',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },

  '& .MuiSelect-select.MuiSelect-multiple': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minHeight: 'unset',
  },

  '& .MuiSelect-icon': {
    color: theme.palette.primary.main,
    right: success || error ? '42px' : '12px',
  },

  '&.Mui-focused .MuiSelect-select': {
    color: theme.palette.primary.main,
  },

  ...(success && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.colors.midGrey,
      borderWidth: '1px',
    },
  }),
}));

const IconContainer = styled(Box)(() => ({
  position: 'absolute',
  right: '14px',
  // Instead of top 50% relative to the entire FormControl, 
  // lock it to the middle of the 48px height select input box
  top: '34%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  zIndex: 1,
}));
const LabelContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '6px',
}));

const StyledLabel = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: tokens.colors.grey,
}));

const OptionalTag = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  color: '#9E9E9E',
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
  marginLeft: 0,
  marginTop: '4px',
  color: tokens.colors.error,
}));

export default function MultiSelect(props: {
  inputs?: { name: string; value: string; isDisabled?: boolean }[];
  values?: string[];
  initValues?: string[];
  isdisabled?: boolean;
  keys?: any;
  label?: string;
  required?: boolean;
  helperText?: string;
  menuPaperSx?: any;
  updateSelection?: (values: string[], keys: any, action: string) => void;
  showSelectAll?: boolean;
  disabledTooltip?: string;
}) {
  const showSelectAll = props.showSelectAll ?? false;
  const theme = useTheme();
  const [lookup, setLookup] = React.useState<{ name: string; value: string; isDisabled?: boolean }[]>(
    props.inputs ? props.inputs : []
  );
  const [values, setValues] = React.useState<string[]>([]);
  const [isFocused, setIsFocused] = React.useState(false);

  const handleChange = (event: SelectChangeEvent<typeof values>) => {
    const {
      target: { value },
    } = event;

    let updValues = typeof value === "string" ? value.split(",") : value;

    // Handle Select All
    if (showSelectAll && updValues.includes("all")) {
      const allSelectableValues = lookup
        .filter((item) => !item.isDisabled)
        .map((item) => item.value);

      const isAllSelected = _.difference(allSelectableValues, values).length === 0;

      const updatedValues = isAllSelected ? [] : allSelectableValues;

      setValues(updatedValues);
      if (props.updateSelection) {
        props.updateSelection(updatedValues, props.keys, isAllSelected ? "DELETE" : "ADD");
      }
      return;
    }

    setValues((prevState) => {
      let action;
      let payload = _.difference(prevState, updValues);
      if (payload.length > 0) action = "DELETE";
      else {
        payload = _.difference(updValues, prevState);
        action = "ADD";
      }

      if (props.updateSelection) {
        props.updateSelection(updValues, props.keys, action);
      }
      return updValues;
    });
  };

  const getSelectedNames = (selected: string[]): string => {
    let result: (string | undefined)[] = [];
    if (props.inputs) {
      result = props.inputs
        .filter((item) => selected.includes(item.value ?? ""))
        .map((item) => item.name);
    }

    if (result.length > 1) {
      return result.sort().join(", ");
    } else if (result.length === 1) {
      return result[0] ?? "";
    } else {
      return props.label ? `Select ${props.label}` : "";
    }
  };

  const valueSelected = (value: string): boolean => {
    return values.includes(value);
  };

  useEffect(() => {
    if (props.inputs && props.inputs.length > 0) {
      setLookup(props.inputs);
    }
  }, [props.inputs]);

  useEffect(() => {
    const newVal = props.values ?? [];
    if (typeof props.values !== "undefined") setValues([...newVal]);
    return () => setValues([]);
  }, []);

  useEffect(() => {
    const newVal = props.initValues ?? [];
    if (typeof props.initValues !== "undefined") {
      setValues([...newVal]);
    }
  }, [props.initValues]);

  const allEnabledItems = lookup.filter((item) => !item.isDisabled).map((item) => item.value);
  const isAllSelected = allEnabledItems.length > 0 && _.difference(allEnabledItems, values).length === 0;
  const isIndeterminate =
    values.length > 0 && _.intersection(allEnabledItems, values).length > 0 && !isAllSelected;

  const getTooltipMessage = () => {
    if (props.disabledTooltip) {
      return props.disabledTooltip;
    }
    if (props.label === "Federal Business Category") {
      return "Select TrackNow Subscription to enable this field";
    }
    if (props.label === "Program Name") {
      return "Select Post Award Subscription to enable this field";
    }
    return "Complete required fields to enable this field";
  };

  const mergedMenuProps = {
    ...MenuProps,
    PaperProps: {
      ...MenuProps.PaperProps,
      sx: props.menuPaperSx,
    },
  };

  const selectComponent = (
    <FormControl
      sx={{ width: "100%", }}
      required={props.required}
    >

      <Select
        disabled={props.isdisabled}
        multiple
        variant="outlined"
        value={values}
        onChange={handleChange}
        renderValue={(selected) => getSelectedNames(selected)}
        MenuProps={mergedMenuProps}
        error={Boolean(props.helperText)}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{
          height: '48px',
          borderRadius: tokens.radius.pill,
          fontFamily: 'Area Normal, Inter, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          backgroundColor: tokens.colors.offWhite,

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.colors.grey,
            borderWidth: '1px',
            borderRadius: tokens.radius.pill,
          },

          '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.primary.main} !important`,
            borderWidth: '2px !important',
            borderRadius: `${tokens.radius.pill}px !important`,
          },

          '&.Mui-disabled': {
            backgroundColor: tokens.colors.offWhite,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.colors.grey,
              borderWidth: '1px',
            },
            '& .MuiSelect-select': {
              color: tokens.colors.grey,
              cursor: 'not-allowed',
              WebkitTextFillColor: tokens.colors.grey,
            },
          },

          '& .MuiSelect-select': {
            // Increase right padding when there's an error to prevent text overlapping both icons
            padding: props.helperText ? '12px 68px 12px 14px !important' : '12px 44px 12px 14px !important',
            color: values.length === 0 ? tokens.colors.grey : tokens.colors.offBlack,
            display: 'block',
            alignItems: 'center',
            boxSizing: 'border-box',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },

          '& .MuiSelect-select.MuiSelect-multiple': {
            display: 'block',
            minHeight: 'unset',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },

          '& .MuiSelect-icon': {
            color: theme.palette.primary.main,
            // Push the arrow left to make room for the error icon on the right
            right: props.helperText ? '40px' : '12px',
          },

          '&.Mui-focused .MuiSelect-select': {
            color: theme.palette.primary.main,
          },
        }}
      >
        {showSelectAll && (
          <MenuItem value="all">
            <TrackNowCheckbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
            />
            <ListItemText
              primary="Select All"
              primaryTypographyProps={{
                fontFamily: 'Area Normal, Inter, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
              }}
            />
          </MenuItem>
        )}

        {props.inputs &&
          props.inputs.map((item) => (
            <MenuItem key={item.name} value={item.value} disabled={item.isDisabled}>
              <TrackNowCheckbox checked={valueSelected(item.value ?? "")} />
              <ListItemText
                primary={item.name}
                primaryTypographyProps={{
                  fontFamily: 'Area Normal, Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              />
            </MenuItem>
          ))}
      </Select>
      {props.helperText && (
        <IconContainer>
          <ReportProblemOutlinedIcon
            sx={{
              color: tokens.colors.error,
              fontSize: '18px',
            }}
          />
        </IconContainer>
      )}
      {props.helperText && (
        <StyledFormHelperText>
          {props.helperText}
        </StyledFormHelperText>
      )}
    </FormControl>
  );

  return (
    <Box>
      {props.label && (
        <LabelContainer>
          <StyledLabel>
            {props.label}
            {props.required && (
              <span style={{ color: tokens.colors.error, marginLeft: "4px" }}>*</span>
            )}
          </StyledLabel>
          {!props.required && isFocused && <OptionalTag>Optional</OptionalTag>}
        </LabelContainer>
      )}
      {props.isdisabled ? (
        <Tooltip title={getTooltipMessage()} arrow>
          <Box>{selectComponent}</Box>
        </Tooltip>
      ) : (
        selectComponent
      )}
    </Box>
  );
}
